import { z } from 'zod';

import {
  hostnameSchema,
  ipv4Schema,
  isoDateTimeSchema,
  positiveIntSchema,
  semverSchema,
  uuidSchema,
} from '../common/primitives';

import { nodeCapacitySchema } from './capacity';
import { preflightReportSchema } from './preflight';

/**
 * Registrierung eines Worker-Nodes beim Master.
 *
 * Ablauf:
 *   1. Ein Administrator legt den Node im Master an — nur mit Region und einer
 *      Notiz — und erhält einen einmaligen Bootstrap-Token mit kurzer
 *      Gültigkeit.
 *   2. Der Agent startet mit `MASTER_URL` + `BOOTSTRAP_TOKEN`, führt seine
 *      Preflight-Prüfungen aus und ruft `POST /agent/v1/enroll` auf.
 *   3. Der Master verbrennt den Token und gibt zwei Langzeit-Credentials aus:
 *        - `nodeToken`  : Agent -> Master (Heartbeat, Fortschrittsmeldungen)
 *        - `agentToken` : Master -> Agent (Befehle)
 *      Beide werden auf Gegenseite nur als Hash gespeichert.
 *
 * Warum zwei Token statt einem: Die Richtungen haben unterschiedliche
 * Lebensdauern und unterschiedliche Angriffsflächen. Ein kompromittierter
 * Worker gibt damit nicht automatisch die Fähigkeit preis, sich als Master
 * auszugeben.
 *
 * Hinweis zur Weiterentwicklung: Diese Token-Authentifizierung ist die
 * Stufe M1. Der Umstieg auf mTLS mit interner CA ist vorgesehen und ändert
 * ausschließlich die Implementierung hinter `AgentAuthGuard` sowie die hier
 * ausgetauschten Felder — nicht den Ablauf. Siehe `docs/adr/0002-agent-auth.md`.
 */

/** Anfrage des Agents zur Registrierung. */
export const enrollmentRequestSchema = z.object({
  /** Einmaliger, kurzlebiger Token aus dem Master. */
  bootstrapToken: z
    .string()
    .min(32, { message: 'Bootstrap-Token ist zu kurz — vermutlich unvollständig kopiert.' })
    .max(512),
  /**
   * Hostname des Servers, wie ihn das Betriebssystem meldet.
   *
   * Er wird **übernommen**, nicht gegen einen vorher eingetippten Wert geprüft:
   * Wie der Server heißt und welche Adressen er trägt, weiß nur er selbst.
   * Abgewiesen wird ausschließlich der Fall, dass ein **anderer** Node den Namen
   * bereits belegt — dann ist eine der beiden Registrierungen ein Versehen.
   */
  hostname: hostnameSchema,
  /** Öffentliche IPv4 — wird für DNS-Einträge (SFTP, Direktzugriff) gebraucht. */
  publicIpv4: ipv4Schema,
  /** Privates Netz — hierüber spricht die Edge mit dem Worker. */
  privateIpv4: ipv4Schema,
  /** Port, auf dem die Agent-API des Workers lauscht. */
  agentPort: positiveIntSchema.max(65_535),
  /** Port, auf dem der lokale Reverse-Proxy HTTP-Verkehr der Edge annimmt. */
  ingressPort: positiveIntSchema.max(65_535),
  //
  // Region und Labels stehen hier bewusst **nicht**.
  //
  // Sie sind Soll-Zustand und gehören dem Master: Er entscheidet, in welcher
  // Region ein Server für den Scheduler zählt und welche Merkmale er trägt. Ein
  // Worker hat darüber nichts auszusagen — er könnte es auch gar nicht wissen.
  // Bis M4a kamen beide aus der Umgebung des Agents und überschrieben bei jeder
  // Neuregistrierung still, was ein Administrator im Master eingestellt hatte.
  //
  /** Version des Agents. Der Master lehnt zu alte Agents ab. */
  agentVersion: semverSchema,
  /** Physische Ausstattung des Servers. */
  capacity: nodeCapacitySchema,
  /**
   * Ergebnis der Systemprüfung. Der Master weist die Registrierung zurück,
   * wenn hier `fail` steht — ein Node ohne funktionierendes Docker soll gar
   * nicht erst in der Flotte auftauchen.
   */
  preflight: preflightReportSchema,
});

export type EnrollmentRequest = z.infer<typeof enrollmentRequestSchema>;

/** Antwort des Masters auf eine erfolgreiche Registrierung. */
export const enrollmentResponseSchema = z.object({
  nodeId: uuidSchema,
  /** Bearer-Token für alle künftigen Agent -> Master-Aufrufe. Nur hier im Klartext. */
  nodeToken: z.string().min(32),
  /** Bearer-Token, mit dem sich der Master beim Agent ausweist. Nur hier im Klartext. */
  agentToken: z.string().min(32),
  /** Fortlaufende Generation; erhöht sich bei jeder Neuregistrierung. */
  generation: positiveIntSchema,
  /** Vom Master vorgegebenes Heartbeat-Intervall in Millisekunden. */
  heartbeatIntervalMs: positiveIntSchema,
  enrolledAt: isoDateTimeSchema,
});

export type EnrollmentResponse = z.infer<typeof enrollmentResponseSchema>;

/**
 * Anfrage zum Erneuern der Credentials.
 * Authentifiziert wird mit dem *aktuellen* `nodeToken`; der Master gibt ein
 * neues Paar aus und invalidiert das alte nach einer Überlappungsfrist.
 */
export const credentialRotationResponseSchema = z.object({
  nodeToken: z.string().min(32),
  agentToken: z.string().min(32),
  rotatedAt: isoDateTimeSchema,
  /** Zeitpunkt, ab dem die alten Token nicht mehr akzeptiert werden. */
  previousTokenValidUntil: isoDateTimeSchema,
});

export type CredentialRotationResponse = z.infer<typeof credentialRotationResponseSchema>;

/** Persistierter Zustand des Agents auf der Platte (`.agent-state/credentials.json`). */
export const agentCredentialsSchema = z.object({
  nodeId: uuidSchema,
  nodeToken: z.string().min(32),
  agentToken: z.string().min(32),
  masterUrl: z.string().url(),
  enrolledAt: isoDateTimeSchema,
  generation: positiveIntSchema,
});

export type AgentCredentials = z.infer<typeof agentCredentialsSchema>;
